import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import type { EventPopup as EventPopupRow } from "../../../lib/supabase";

interface EventPopupProps {
  isKr: boolean;
}

const EventPopup = ({ isKr }: EventPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<"event" | "form">("event");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [popupData, setPopupData] = useState<EventPopupRow | null>(null);

  useEffect(() => {
    const fetchActivePopup = async () => {
      try {
        // Simple query - just fetch latest active popup (skip complex date filtering)
        const { data, error } = await supabase
          .from("event_popups")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.warn("[EventPopup] fetch error:", error.message);
        }

        if (data) {
          setPopupData(data as EventPopupRow);
        }
      } catch (err) {
        console.warn("[EventPopup] unexpected error:", err);
      }
    };

    fetchActivePopup();

    // Realtime: re-fetch whenever event_popups changes (admin updates)
    const channel = supabase
      .channel("event_popups_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_popups" },
        () => { fetchActivePopup(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (popupData === null) return; // still loading
    const dismissed = sessionStorage.getItem("eventPopupDismissed");
    if (!dismissed) {
      const delay = (popupData?.show_delay_seconds ?? 1) * 1000;
      const timer = setTimeout(() => setIsOpen(true), delay);
      return () => clearTimeout(timer);
    }
  }, [popupData]);

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(true);
    sessionStorage.setItem("eventPopupDismissed", "1");
  };

  const handleReopen = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const fields: Record<string, string> = {};
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) fields[el.name] = el.value;
    }
    try {
      // Save to Supabase event_registrations
      await supabase.from("event_registrations").insert({
        event_id: popupData?.id ?? "",
        event_name: isKr ? (popupData?.title_kr || popupData?.title_vi) : popupData?.title_vi,
        name: fields.name ?? "",
        email: fields.email ?? "",
        phone: fields.phone ?? "",
        message: fields.message ?? "",
        source: "popup",
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  // If no active popup from Supabase, don't show anything
  if (!popupData) return null;

  const titleText = isKr ? (popupData.title_kr || popupData.title_vi) : popupData.title_vi;
  const shortDesc = isKr
    ? ((popupData as EventPopupRow & { short_description_kr?: string }).short_description_kr || (popupData as EventPopupRow & { short_description_vi?: string }).short_description_vi || "")
    : ((popupData as EventPopupRow & { short_description_vi?: string }).short_description_vi || "");
  const highlights = popupData.highlights ?? [];
  const bgColor = popupData.bg_color ?? "#004A99";
  // Link to event detail page
  const eventDetailLink = `/su-kien/${popupData.id}`;

  return (
    <>
      {/* Always visible small button – shows whenever popup is not open */}
      {!isOpen && (
        <button
          onClick={handleReopen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 flex flex-col items-center justify-center text-white rounded-full transition-all cursor-pointer group hover:scale-110"
          style={{ backgroundColor: bgColor }}
          title={isKr ? "이벤트 보기" : "Xem sự kiện"}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-calendar-event-fill text-lg" />
          </div>
          <span className="text-[8px] font-bold mt-0.5 whitespace-nowrap">EVENT</span>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
            <span className="text-[9px] font-black text-white">!</span>
          </span>
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6">
          <div
            className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-[560px] flex flex-col"
            style={{ animation: "fadeInScale 0.4s ease forwards", maxHeight: "90vh" }}
          >
            {/* Header — fixed, không scroll */}
            <div className="relative p-5 overflow-hidden flex-shrink-0" style={{ backgroundColor: bgColor }}>
              {popupData.image_url && (
                <div className="absolute inset-0 opacity-20">
                  <img src={popupData.image_url} alt="" className="w-full h-full object-cover object-top" />
                </div>
              )}
              {/* Close button — luôn hiện ở góc phải trên */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors cursor-pointer"
                aria-label="Đóng popup"
              >
                <i className="ri-close-line text-base" />
              </button>

              <div className="relative z-10 pr-10">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-[#00AEEF] text-white text-xs px-2.5 py-1 rounded-full font-bold">
                    <i className="ri-fire-line text-xs" />
                    {isKr ? "진행 중 이벤트" : "SỰ KIỆN ĐANG DIỄN RA"}
                  </span>
                  {popupData.event_date && (
                    <span className="text-white/60 text-xs whitespace-nowrap">{popupData.event_date}</span>
                  )}
                </div>
                <h2 className="text-lg font-black text-white leading-tight">{titleText}</h2>
                {/* Mô tả ngắn và ảnh banner ĐÃ BỊ ẨN khỏi header — chỉ hiện trong body */}
              </div>

              {/* Tabs */}
              <div className="relative z-10 flex gap-1 mt-4 bg-white/10 rounded-lg p-1">
                {(["event", "form"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white text-[#004A99]" : "text-white/70 hover:text-white"}`}
                  >
                    <i className={`${tab === "event" ? "ri-calendar-event-line" : "ri-user-add-line"} mr-1`} />
                    {tab === "event" ? (isKr ? "이벤트 안내" : "Thông Tin Sự Kiện") : (isKr ? "참여 신청" : "Đăng Ký Tham Gia")}
                  </button>
                ))}
              </div>
            </div>

            {/* Content — scrollable */}
            <div className="overflow-y-auto flex-1 p-5">
              {activeTab === "event" ? (
                <div>
                  {/* Banner image */}
                  {popupData.image_url && (
                    <div className="w-full rounded-xl overflow-hidden mb-4" style={{ height: 180 }}>
                      <img src={popupData.image_url} alt={titleText} className="w-full h-full object-cover object-top" />
                    </div>
                  )}

                  {/* Short description — hiển thị nổi bật */}
                  {shortDesc && (
                    <div className="bg-[#EDF2FB] border-l-4 border-[#004A99] rounded-r-xl px-4 py-3 mb-4">
                      <p className="text-sm text-[#212529] leading-relaxed">{shortDesc}</p>
                    </div>
                  )}

                  {/* Event meta: date + location */}
                  {(popupData.event_date || popupData.event_location_vi) && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {popupData.event_date && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-[#F8F9FA] rounded-xl">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-calendar-line text-[#004A99] text-sm" />
                          </div>
                          <p className="text-xs font-semibold text-[#212529]">{popupData.event_date}</p>
                        </div>
                      )}
                      {(popupData.event_location_vi || popupData.event_location_kr) && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-[#F8F9FA] rounded-xl">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-map-pin-line text-[#004A99] text-sm" />
                          </div>
                          <p className="text-xs font-semibold text-[#212529]">
                            {isKr ? (popupData.event_location_kr ?? popupData.event_location_vi) : popupData.event_location_vi}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Highlights */}
                  {highlights.length > 0 && (
                    <div className="bg-[#004A99]/5 border border-[#004A99]/15 rounded-xl p-4 mb-4">
                      <p className="text-xs font-bold text-[#004A99] mb-2">
                        {isKr ? "🎯 이벤트 하이라이트" : "🎯 Điểm Nổi Bật"}
                      </p>
                      <ul className="space-y-1.5">
                        {(highlights as string[]).map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-[#212529]">
                            <i className="ri-checkbox-circle-fill text-[#00AEEF] mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Link to event detail page */}
                  <a
                    href={eventDetailLink}
                    className="w-full py-3 font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm text-white flex items-center justify-center gap-2 mb-3"
                    style={{ backgroundColor: bgColor }}
                  >
                    <i className="ri-arrow-right-circle-line" />
                    {isKr ? "자세히 보기" : "Tham khảo chi tiết tại đây"}
                  </a>

                  <button
                    onClick={() => setActiveTab("form")}
                    className="w-full py-3 font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm border-2 flex items-center justify-center gap-2"
                    style={{ borderColor: bgColor, color: bgColor }}
                  >
                    <i className="ri-user-add-line" />
                    {isKr ? "지금 바로 참여 신청하기 →" : "Đăng Ký Tham Gia Ngay →"}
                  </button>
                </div>
              ) : (
                <div>
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 flex items-center justify-center bg-[#00AEEF]/10 rounded-full mx-auto mb-4">
                        <i className="ri-checkbox-circle-fill text-4xl text-[#00AEEF]" />
                      </div>
                      <h3 className="text-lg font-bold text-[#212529] mb-2">{isKr ? "신청이 완료되었습니다!" : "Đăng ký thành công!"}</h3>
                      <p className="text-sm text-gray-500">{isKr ? "곧 담당자가 연락드릴 예정입니다." : "Chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất."}</p>
                    </div>
                  ) : (
                    <form id="event-consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-[#212529] mb-1">{isKr ? "이름 *" : "Họ và tên *"}</label>
                        <input type="text" name="name" required placeholder={isKr ? "홍길동" : "Nguyễn Văn A"} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#004A99] bg-[#F8F9FA] text-[#212529] placeholder-gray-400" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#212529] mb-1">Email *</label>
                        <input type="email" name="email" required placeholder="email@company.com" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#004A99] bg-[#F8F9FA] text-[#212529] placeholder-gray-400" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#212529] mb-1">{isKr ? "전화번호" : "Số điện thoại"}</label>
                        <input type="tel" name="phone" placeholder={isKr ? "010-0000-0000" : "0901 000 000"} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#004A99] bg-[#F8F9FA] text-[#212529] placeholder-gray-400" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#212529] mb-1">{isKr ? "문의 내용" : "Nội dung thắc mắc"}</label>
                        <textarea name="message" rows={3} maxLength={500} placeholder={isKr ? "궁금하신 점을 입력해 주세요. (최대 500자)" : "Nhập nội dung thắc mắc... (tối đa 500 ký tự)"} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#004A99] resize-none bg-[#F8F9FA] text-[#212529] placeholder-gray-400" />
                      </div>
                      <button type="submit" disabled={submitting} className="w-full py-3 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: bgColor }}>
                        {submitting ? <><i className="ri-loader-4-line animate-spin" />{isKr ? "전송 중..." : "Đang gửi..."}</> : <><i className="ri-send-plane-line" />{isKr ? "신청하기" : "Gửi Đăng Ký"}</>}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventPopup;
